/* Ellips cart drawer (store rebuild 2026-09-24). Contract: build/REBUILD-SPEC.md §4.

   window.ElCart
     add(items, { open = true, context })   items: [{ id, quantity, selling_plan?, properties? }] (or one object)
     change(lineKeyOrLine, { quantity, selling_plan })   selling_plan: id, or null for one-time
     open() / close() / refresh()
     cart   last cart JSON seen by the drawer (null until the first change)
   Every mutation resolves with the cart JSON (as /cart.js) and dispatches `el:cart:changed` on document
   (detail: cart). On failure the Shopify error text shows in the drawer and the promise rejects.

   The drawer HTML is sections/el-cart-drawer.liquid, rendered with the page and re-rendered through the Section
   Rendering API with every mutation (sections: 'el-cart-drawer', sections_url: location.pathname). Only
   [data-el-cart-inner] is swapped, so the open state, focus trap and live region survive.

   Gifts: a non-gift line on a selling plan (a Starter jar) brings the Ritual Kit (the variants listed in the section's
   #el-cart-config "gifts", from settings.el_gift_products, each quantity 1 with property _el_gift: "1"); with no plan
   line the gift lines go. The WELCOME GIFT automatic discount makes them free. Line keys change whenever Shopify
   re-evaluates discounts, so every fix is computed from the latest cart, never from cached keys.
   "giftAny" in #el-cart-config (2026-10-26, build/cart-gift-1026, ellipskauppa.fi / ellipshair.se): the gifts ride along
   with any product line instead (a 0-price first-order gift the warehouse reads from the order).

   Mutations run one at a time (a promise queue); drawer controls ignore clicks while one is running.

   Dawn and Shopify compatibility: the section renders an empty <cart-drawer data-el-cart-proxy> that Dawn's product
   form / quick add and Shopify.actions.openCart (assets/standard-actions-override.js) find and call; it forwards to
   this drawer. Changes are published on Dawn's pubsub ('cart-update', source 'el-cart') so Dawn's /cart page
   re-renders itself, and anyone else's 'cart-update' (Shopify.actions.updateCart, the /cart page) refreshes the drawer. */
(() => {
  'use strict';
  if (window.ElCart) return;

  const SECTION = 'el-cart-drawer';
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  const CONTROLS = '[data-el-cart-close], [data-el-cart-qty], [data-el-cart-remove], [data-el-cart-plan], [data-el-cart-add]';

  const url = (path) => {
    if (typeof window.ellipsUrl === 'function') return window.ellipsUrl(path);
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root + String(path).replace(/^\//, '');
  };
  const $ = (sel, root) => (root || document).querySelector(sel);

  let shell = null; // #el-cart, persistent
  let lastCart = null; // latest cart JSON
  let lastFocus = null; // focus before open()
  let chain = Promise.resolve();
  let pending = 0;

  const inner = () => (shell ? $('[data-el-cart-inner]', shell) : null);
  const panel = () => (shell ? $('[data-el-cart-panel]', shell) : null);
  const isOpen = () => !!(shell && shell.classList.contains('is-open'));

  function config() {
    const el = shell ? $('#el-cart-config', shell) : null;
    if (!el) return {};
    try {
      return JSON.parse(el.textContent) || {};
    } catch (e) {
      return {};
    }
  }
  const strings = () => config().strings || {};
  /* Theme settings > Ellips shop > "Sell subscriptions" off (2026-10-09): adds go in one-time and a plan line left in
     an older cart is switched to one-time (planFix); with no plan line the Ritual Kit goes, as giftFix always did. */
  const sellPlans = () => config().sellPlans !== false;
  const giftIds = () => (config().gifts || []).map(Number).filter(Boolean);
  const giftAny = () => config().giftAny === true;
  /* Try sizes (2026-10-01) can be on a subscription too; only a jar on a plan brings the Ritual Kit. */
  const tryIds = () => (config().tryVariants || []).map(Number).filter(Boolean);

  /* ---- cart lines, from cart JSON or from the server-rendered config ------------------------------------------ */

  function isGift(item, gifts) {
    if (item.properties && item.properties._el_gift) return true;
    if (String(item.handle || '').indexOf('welcome-gift-') === 0) return true;
    return gifts.indexOf(Number(item.variant_id)) !== -1;
  }

  function linesOf(cart) {
    const gifts = giftIds();
    const tries = tryIds();
    if (cart && Array.isArray(cart.items)) {
      return cart.items.map((i) => ({
        key: i.key,
        variant: Number(i.variant_id),
        qty: Number(i.quantity),
        gift: isGift(i, gifts),
        try: tries.indexOf(Number(i.variant_id)) !== -1 || /-sachet$/.test(String(i.handle || '')),
        plan: i.selling_plan_allocation ? Number(i.selling_plan_allocation.selling_plan.id) : 0,
      }));
    }
    return (config().lines || []).map((l) => ({
      key: l.key,
      variant: Number(l.variant),
      qty: Number(l.qty),
      gift: !!l.gift,
      try: !!l.try,
      plan: Number(l.plan) || 0,
    }));
  }

  const countOf = (lines) => lines.reduce((n, l) => n + (l.gift ? 0 : l.qty), 0);

  /* What the cart needs so that gifts ride along with a Starter jar only: { updates: {key: qty}, adds: [items] }. */
  function giftFix(lines) {
    const gifts = giftIds();
    if (!gifts.length) return null;
    const starter = giftAny() ? lines.some((l) => !l.gift && l.qty > 0) : lines.some((l) => l.plan && !l.gift && !l.try);
    const updates = {};
    const adds = [];
    const seen = [];
    lines.forEach((l) => {
      if (!l.gift) return;
      if (!starter || gifts.indexOf(l.variant) === -1 || seen.indexOf(l.variant) !== -1) {
        updates[l.key] = 0;
        return;
      }
      seen.push(l.variant);
      if (l.qty !== 1) updates[l.key] = 1;
    });
    if (starter) {
      gifts.forEach((id) => {
        if (seen.indexOf(id) === -1) adds.push(giftItem(id));
      });
    }
    return Object.keys(updates).length || adds.length ? { updates, adds } : null;
  }

  const giftItem = (id) => ({ id, quantity: 1, properties: { _el_gift: '1' } });

  /* Subscriptions off: the first non-gift line still on a plan, or null. */
  const planFix = (lines) => (sellPlans() ? null : lines.find((l) => l.plan && !l.gift) || null);

  /* Gifts missing from the current cart, added in the same request as a Starter jar. */
  function missingGifts() {
    const gifts = giftIds();
    if (!gifts.length) return [];
    const have = linesOf(lastCart).filter((l) => l.gift).map((l) => l.variant);
    return gifts.filter((id) => have.indexOf(id) === -1).map(giftItem);
  }

  /* ---- requests --------------------------------------------------------------------------------------------- */

  function failure(data, status) {
    let msg = '';
    if (data) {
      if (typeof data.description === 'string') msg = data.description;
      else if (typeof data.message === 'string' && data.message !== 'Cart Error') msg = data.message;
      else if (typeof data.errors === 'string') msg = data.errors;
    }
    const err = new Error(msg || 'Cart request failed (' + status + ')');
    err.userMessage = msg;
    err.status = status;
    return err;
  }

  async function request(path, body) {
    const init =
      body === undefined
        ? { headers: { Accept: 'application/json' }, credentials: 'same-origin' }
        : {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify(body),
          };
    let res;
    try {
      res = await fetch(url(path), init);
    } catch (e) {
      throw failure(null, 0);
    }
    let data = null;
    try {
      data = await res.json();
    } catch (e) {
      data = null; // e.g. the bot check's HTML page (429)
    }
    if (!res.ok || !data) throw failure(data, res.status);
    return data;
  }

  const withSection = (body) => Object.assign(body, { sections: SECTION, sections_url: window.location.pathname });
  const htmlOf = (data) => (data && data.sections && data.sections[SECTION]) || null;

  /* No "Accept: application/json" here: on product, collection and cart URLs Shopify answers that header with the
     page's own JSON (product / collection / cart), not the section render. */
  async function fetchSection() {
    const res = await fetch(window.location.pathname + '?sections=' + SECTION, {
      credentials: 'same-origin',
      cache: 'no-store',
    });
    if (!res.ok) throw failure(null, res.status);
    const data = await res.json();
    return data[SECTION] || null;
  }

  /* ---- rendering -------------------------------------------------------------------------------------------- */

  function swap(html) {
    const cur = inner();
    if (!html || !cur) return false;
    const fresh = $('[data-el-cart-inner]', new DOMParser().parseFromString(html, 'text/html'));
    if (!fresh) return false;
    const scroller = $('[data-el-cart-scroll]', cur);
    const top = scroller ? scroller.scrollTop : 0;
    const active = document.activeElement;
    const hadFocus = !!(active && active !== cur && cur.contains(active));
    const focusKey = hadFocus ? active.getAttribute('data-el-focus') : null;
    cur.innerHTML = fresh.innerHTML;
    const next = $('[data-el-cart-scroll]', cur);
    if (next) next.scrollTop = top;
    if (hadFocus) {
      const esc = window.CSS && CSS.escape ? CSS.escape(focusKey || '') : focusKey;
      const target = (focusKey && cur.querySelector('[data-el-focus="' + esc + '"]')) || panel();
      if (target) target.focus({ preventScroll: true });
    }
    return true;
  }

  function paintCount(n) {
    document.querySelectorAll('[data-el-cart-count]').forEach((el) => {
      el.textContent = String(n);
      el.hidden = n === 0;
    });
  }

  function say(text) {
    const el = shell ? $('[data-el-cart-status]', shell) : null;
    if (!el || !text) return;
    el.textContent = '';
    window.setTimeout(() => {
      el.textContent = text;
    }, 80);
  }

  function showError(err) {
    const el = shell ? $('[data-el-cart-error]', shell) : null;
    if (!el) return;
    const msg = (err && err.userMessage) || strings().error || '';
    el.textContent = msg;
    el.hidden = !msg;
  }

  function busy(on) {
    if (!shell) return;
    shell.classList.toggle('is-busy', on);
    const p = panel();
    if (p) p.setAttribute('aria-busy', on ? 'true' : 'false');
  }

  function enqueue(task) {
    pending += 1;
    busy(true);
    const run = chain.then(task);
    chain = run
      .catch(() => {})
      .then(() => {
        pending -= 1;
        if (!pending) busy(false);
      });
    return run;
  }

  /* ---- Shopify standard events (the same ones Dawn's cart and product form dispatch) ----------------------- */

  function linesEvent(action, lines, context) {
    const E = window.StandardEvents && window.StandardEvents.CartLinesUpdateEvent;
    if (!E || !shell) return null;
    try {
      const deferred = E.createPromise();
      const ev = new E({ action, context, lines, promise: deferred.promise });
      if (ev.promise && typeof ev.promise.catch === 'function') ev.promise.catch(() => {});
      shell.dispatchEvent(ev);
      return deferred;
    } catch (e) {
      return null;
    }
  }

  function settleEvent(deferred, cart, err) {
    const SE = window.StandardEvents || {};
    if (deferred) {
      try {
        if (err) deferred.reject(err);
        else deferred.resolve({ cart: SE.CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
      } catch (e) {
        /* analytics only */
      }
    }
    if (err && SE.CartErrorEvent && shell) {
      try {
        shell.dispatchEvent(new SE.CartErrorEvent({ error: err.userMessage || err.message, code: 'INVALID' }));
      } catch (e) {
        /* analytics only */
      }
    }
  }

  function viewEvent() {
    const E = window.StandardEvents && window.StandardEvents.CartViewEvent;
    const el = shell ? $('[data-el-cart-view]', shell) : null;
    if (!E || !el) return;
    try {
      const payload = JSON.parse(el.textContent);
      if (payload && payload.cart) (panel() || shell).dispatchEvent(new E(payload));
    } catch (e) {
      /* analytics only */
    }
  }

  /* ---- core ------------------------------------------------------------------------------------------------ */

  async function applyFix(fix) {
    let cart = null;
    let html = null;
    if (Object.keys(fix.updates).length) {
      const data = await request('cart/update.js', withSection({ updates: fix.updates }));
      cart = data;
      html = htmlOf(data) || html;
    }
    if (fix.adds.length) {
      /* Reversed so the cart (newest first) lists them in settings order. */
      const adds = fix.adds.slice().reverse();
      let data = null;
      try {
        data = await request('cart/add.js', withSection({ items: adds }));
      } catch (e) {
        for (const item of adds) {
          try {
            data = await request('cart/add.js', withSection({ items: [item] }));
          } catch (e2) {
            /* a gift that cannot be added is skipped, never blocks the cart */
          }
        }
      }
      if (data) html = htmlOf(data) || html;
      cart = await request('cart.js');
    }
    return { cart, html };
  }

  /* After a mutation: get the full cart, fix the gifts, render once, announce. */
  async function commit(data, message) {
    let cart = data && data.token && Array.isArray(data.items) ? data : null; // change.js / update.js return the cart
    let html = htmlOf(data);
    if (!cart) cart = await request('cart.js'); // add.js returns only the added lines (and settle() passes none)
    /* One line per request, re-read after each: line keys change when a line's plan changes. */
    for (let n = 0, pl = planFix(linesOf(cart)); pl && n < 10; n += 1, pl = planFix(linesOf(cart))) {
      try {
        const r = await request('cart/change.js', { id: pl.key, quantity: pl.qty, selling_plan: null });
        cart = r && Array.isArray(r.items) ? r : await request('cart.js');
        html = null;
      } catch (e) {
        if (window.console) console.warn('[ElCart] plan removal failed', e);
        break;
      }
    }
    const fix = giftFix(linesOf(cart));
    if (fix) {
      try {
        const r = await applyFix(fix);
        if (r.cart) cart = r.cart;
        if (r.html) html = r.html;
      } catch (e) {
        if (window.console) console.warn('[ElCart] gift sync failed', e);
      }
    }
    if (!swap(html)) swap(await fetchSection());
    finish(cart, message);
    if (data && data.errors) showError({ userMessage: typeof data.errors === 'string' ? data.errors : '' });
    return cart;
  }

  function finish(cart, message) {
    lastCart = cart;
    paintCount(countOf(linesOf(cart)));
    if (message) say(message);
    document.dispatchEvent(new CustomEvent('el:cart:changed', { detail: cart }));
    if (typeof window.publish === 'function') {
      try {
        window.publish('cart-update', { source: 'el-cart', cartData: cart });
      } catch (e) {
        /* Dawn subscribers only */
      }
    }
  }

  /* A cart changed outside this script (a no-JS post, a Dawn form, Shopify.actions): fix the gifts from the server
     cart and render once. `html` is a drawer render the caller already holds. */
  const settle = (html) => commit(html ? { sections: { [SECTION]: html } } : null, '');

  function normalize(items) {
    const list = Array.isArray(items) ? items : items ? [items] : [];
    return list
      .map((i) => {
        const item = { id: Number(i.id || i.variant_id), quantity: Math.max(1, parseInt(i.quantity, 10) || 1) };
        if (i.selling_plan && sellPlans()) item.selling_plan = Number(i.selling_plan);
        if (i.properties && typeof i.properties === 'object') item.properties = i.properties;
        return item;
      })
      .filter((i) => i.id);
  }

  function add(items, options) {
    const opts = Object.assign({ open: true, context: 'product' }, options || {});
    const list = normalize(items);
    return enqueue(async () => {
      const ev = linesEvent('add', list.map((i) => ({ merchandiseId: i.id, quantity: i.quantity })), opts.context);
      try {
        if (!list.length) throw failure(null, 400);
        const tries = tryIds();
        const brings = giftAny() ? list.length > 0 : list.some((i) => i.selling_plan && tries.indexOf(Number(i.id)) === -1);
        const extra = brings ? missingGifts() : [];
        /* The cart lists the newest line first and a multi-item add lands in reverse order: send the gifts first
           (reversed) so the jar ends on top and the kit reads in settings order. */
        const send = extra.slice().reverse().concat(list.slice().reverse());
        let data;
        try {
          data = await request('cart/add.js', withSection({ items: send }));
        } catch (err) {
          if (!extra.length) throw err;
          data = await request('cart/add.js', withSection({ items: list.slice().reverse() }));
        }
        const cart = await commit(data, strings().added);
        if (opts.open) open();
        settleEvent(ev, cart);
        return cart;
      } catch (err) {
        showError(err);
        if (opts.open) open();
        settleEvent(ev, null, err);
        throw err;
      }
    });
  }

  function change(line, options) {
    const opts = options || {};
    return enqueue(async () => {
      const body = {};
      if (typeof line === 'number') body.line = line;
      else body.id = String(line);
      let qty = opts.quantity;
      if (qty === undefined || qty === null) {
        const cur = linesOf(lastCart).find((l) => l.key === body.id);
        qty = cur ? cur.qty : undefined;
      }
      if (qty !== undefined) body.quantity = Math.max(0, parseInt(qty, 10) || 0);
      if (Object.prototype.hasOwnProperty.call(opts, 'selling_plan')) body.selling_plan = opts.selling_plan && sellPlans() ? Number(opts.selling_plan) : null;
      const ev = linesEvent(body.quantity === 0 ? 'remove' : 'update', [{ id: body.id || String(line), quantity: body.quantity }], 'cart');
      try {
        const data = await request('cart/change.js', withSection(body));
        const cart = await commit(data, strings().updated);
        settleEvent(ev, cart);
        return cart;
      } catch (err) {
        showError(err);
        settleEvent(ev, null, err);
        throw err;
      }
    });
  }

  function refresh() {
    return enqueue(async () => {
      const html = await fetchSection();
      swap(html);
      lastCart = null; // the fresh config is now the truth
      paintCount(countOf(linesOf(null)));
      if (planFix(linesOf(null)) || giftFix(linesOf(null))) await settle();
    });
  }

  /* ---- open / close / focus / scroll lock ------------------------------------------------------------------- */

  function lock(on) {
    const root = document.documentElement;
    if (on) {
      const gap = window.innerWidth - root.clientWidth;
      root.classList.add('el-cart-lock');
      if (gap > 0) document.body.style.paddingRight = gap + 'px';
    } else {
      root.classList.remove('el-cart-lock');
      document.body.style.paddingRight = '';
    }
  }

  function open() {
    init();
    if (!shell) {
      window.location.href = url('cart');
      return;
    }
    if (isOpen()) return;
    lastFocus = document.activeElement;
    shell.removeAttribute('inert');
    shell.setAttribute('aria-hidden', 'false');
    void shell.offsetWidth; // start the slide from the closed position
    shell.classList.add('is-open');
    lock(true);
    const p = panel();
    if (p) window.requestAnimationFrame(() => p.focus({ preventScroll: true }));
    viewEvent();
  }

  function close() {
    if (!isOpen()) return;
    shell.classList.remove('is-open');
    shell.setAttribute('aria-hidden', 'true');
    shell.setAttribute('inert', '');
    lock(false);
    if (lastFocus && document.contains(lastFocus) && typeof lastFocus.focus === 'function') lastFocus.focus({ preventScroll: true });
    lastFocus = null;
  }

  function focusables() {
    const p = panel();
    if (!p) return [];
    return Array.prototype.filter.call(p.querySelectorAll(FOCUSABLE), (el) => el.getClientRects().length > 0);
  }

  function onKeydown(e) {
    if (!isOpen()) return;
    if (e.key === 'Escape' || e.key === 'Esc') {
      e.preventDefault();
      close();
      return;
    }
    if (e.key !== 'Tab') return;
    const p = panel();
    const list = focusables();
    if (!list.length) {
      e.preventDefault();
      if (p) p.focus();
      return;
    }
    const first = list[0];
    const last = list[list.length - 1];
    const a = document.activeElement;
    if (e.shiftKey && (a === first || a === p || !p.contains(a))) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && (a === last || !p.contains(a))) {
      e.preventDefault();
      first.focus();
    }
  }

  function onFocusin(e) {
    if (!isOpen()) return;
    const p = panel();
    if (p && !p.contains(e.target)) p.focus({ preventScroll: true });
  }

  /* ---- clicks and the checkout form --------------------------------------------------------------------------- */

  function withLoading(btn, job, revert) {
    btn.classList.add('is-loading');
    btn.setAttribute('aria-busy', 'true');
    const done = () => {
      btn.classList.remove('is-loading');
      btn.removeAttribute('aria-busy');
    };
    job.then(done, () => {
      done();
      if (revert) revert();
    });
  }

  function onClick(e) {
    const opener = e.target.closest ? e.target.closest('[data-el-cart-open]') : null;
    if (opener && !(shell && shell.contains(opener))) {
      if (!shell || e.defaultPrevented || e.button > 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      e.preventDefault();
      open();
      return;
    }
    if (!shell || !e.target.closest || !shell.contains(e.target)) return;
    /* A drawer link to the page already open (e.g. "Shop all products" on All products) just closes the drawer. */
    const link = e.target.closest('a[href]');
    if (link && shell.contains(link) && link.pathname === window.location.pathname && !link.hash && !e.metaKey && !e.ctrlKey) {
      e.preventDefault();
      close();
      return;
    }
    const t = e.target.closest(CONTROLS);
    if (!t || !shell.contains(t)) return;
    e.preventDefault();
    if (t.hasAttribute('data-el-cart-close')) {
      close();
      return;
    }
    if (pending || t.getAttribute('aria-disabled') === 'true') return;
    const key = t.getAttribute('data-key');
    if (t.hasAttribute('data-el-cart-qty')) {
      withLoading(t, change(key, { quantity: Number(t.getAttribute('data-qty')) }));
    } else if (t.hasAttribute('data-el-cart-remove')) {
      withLoading(t, change(key, { quantity: 0 }));
    } else if (t.hasAttribute('data-el-cart-plan')) {
      const on = t.getAttribute('aria-checked') === 'true';
      const plan = t.getAttribute('data-plan');
      if (!on && !plan) return;
      t.setAttribute('aria-checked', on ? 'false' : 'true'); // answer the tap at once; the re-render confirms it
      const job = change(key, { quantity: Number(t.getAttribute('data-qty')) || 1, selling_plan: on ? null : Number(plan) });
      withLoading(t, job, () => t.setAttribute('aria-checked', on ? 'true' : 'false'));
    } else if (t.hasAttribute('data-el-cart-add')) {
      const item = { id: Number(t.getAttribute('data-variant')), quantity: 1 };
      const plan = t.getAttribute('data-plan');
      if (plan) item.selling_plan = Number(plan);
      withLoading(t, add([item], { open: true, context: 'cart' }));
    }
  }

  function submitCheckout() {
    const form = shell ? $('[data-el-cart-form]', shell) : null;
    if (!form) return;
    const btn = form.querySelector('[name="checkout"]');
    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit(btn || undefined);
    } else {
      const flag = document.createElement('input');
      flag.type = 'hidden';
      flag.name = 'checkout';
      form.appendChild(flag);
      form.submit();
    }
  }

  function onSubmit(e) {
    const form = e.target;
    if (!shell || !form || !form.matches || !form.matches('[data-el-cart-form]') || !shell.contains(form)) return;
    const btn = form.querySelector('[name="checkout"]');
    if (btn) {
      btn.classList.add('is-loading');
      btn.setAttribute('aria-busy', 'true');
    }
    /* Never check out a cart that is still changing (e.g. gifts on their way): wait, then submit the fresh form. */
    if (pending) {
      e.preventDefault();
      chain.then(submitCheckout);
    }
  }

  /* ---- start ----------------------------------------------------------------------------------------------- */

  function init() {
    if (shell) return;
    shell = document.getElementById('el-cart');
    if (!shell) return;
    paintCount(countOf(linesOf(null)));
    wireDawn();
    /* A cart built without this script (a no-JS form post, an old buy button) may hold a Starter jar without its
       gifts or gifts without a jar: settle it once on load (Dawn's /cart page follows through the pubsub). */
    if (planFix(linesOf(null)) || giftFix(linesOf(null))) enqueue(() => settle()).catch(() => {});
  }

  /* The <cart-drawer data-el-cart-proxy> stand-in and Dawn's pubsub (see the header comment). */
  function wireDawn() {
    const proxy = document.querySelector('cart-drawer[data-el-cart-proxy]');
    if (proxy && !proxy.renderContents) {
      proxy.open = () => open();
      proxy.close = () => close();
      proxy.getSectionsToRender = () => [{ id: SECTION }];
      proxy.setActiveElement = (el) => {
        if (el && typeof el.focus === 'function') lastFocus = el;
      };
      proxy.renderContents = (response) => {
        const opener = lastFocus;
        enqueue(() => settle(htmlOf(response)))
          .catch(() => {})
          .then(() => {
            open();
            if (opener) lastFocus = opener;
          });
      };
    }
    if (typeof window.subscribe === 'function') {
      window.subscribe('cart-update', (event) => {
        const source = event && event.source;
        if (source === 'el-cart' || source === 'product-form') return; // our own, or renderContents() follows
        refresh().catch(() => {});
      });
    }
  }

  document.addEventListener('click', onClick);
  document.addEventListener('keydown', onKeydown);
  document.addEventListener('focusin', onFocusin);
  document.addEventListener('submit', onSubmit);
  window.addEventListener('pageshow', (e) => {
    if (!e.persisted || !shell) return;
    shell.querySelectorAll('.is-loading').forEach((b) => {
      b.classList.remove('is-loading');
      b.removeAttribute('aria-busy');
    });
    refresh().catch(() => {});
  });

  window.ElCart = {
    add,
    change,
    open,
    close,
    refresh,
    get cart() {
      return lastCart;
    },
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
